import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useScrollReveal } from "@/hooks/useScrollReveal";

function toDateKey(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function buildMonthGrid(year: number, month: number) {
  const first = new Date(year, month, 1);
  const startWeekday = first.getDay(); // 0 = Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: (Date | null)[] = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function AvailabilityCalendar() {
  const sectionRef = useScrollReveal();
  const today = useMemo(() => new Date(), []);
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [unavailable, setUnavailable] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const { data } = await supabase.from("unavailable_dates").select("date");
      if (cancelled || !data) return;
      setUnavailable(new Set(data.map((r: { date: string }) => r.date)));
    };
    load();

    const channel = supabase
      .channel("unavailable_dates_public")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "unavailable_dates" },
        () => load(),
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, []);

  const grid = buildMonthGrid(cursor.getFullYear(), cursor.getMonth());
  const todayKey = toDateKey(today);

  return (
    <section
      id="availability"
      ref={sectionRef}
      className="overflow-hidden py-20 lg:py-28 px-6 md:px-12"
      style={{ backgroundColor: "#F5F0E8" }}
    >
      <div className="max-w-3xl mx-auto text-center">
        <p className="fade-in text-base tracking-[0.1em] uppercase font-sans font-medium mb-6" style={{ color: "#4E4228" }}>
          Availability
        </p>
        <h2
          className="fade-in delay-100 font-serif font-light leading-tight mb-6"
          style={{ fontSize: "clamp(2rem, 3vw, 3rem)", color: "#3C3530" }}
        >
          Check Availability
        </h2>
        <div className="fade-in delay-200 w-7 h-px mx-auto mb-8" style={{ backgroundColor: "#A8B09E" }}></div>
        <p className="fade-in delay-300 font-sans font-light leading-loose text-sm mb-12" style={{ color: "#8C8478" }}>
          See which dates are open. To enquire about a stay, please use the form below — all bookings are
          confirmed personally by the owner via WhatsApp and email.
        </p>
      </div>

      <div className="max-w-3xl mx-auto fade-in delay-300">
        <div
          className="rounded-lg p-6 md:p-10"
          style={{ backgroundColor: "#FDFAF4", border: "1px solid #E4DDD4" }}
        >
          {/* Month nav */}
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
              className="w-10 h-10 flex items-center justify-center rounded-full transition-colors cursor-pointer"
              style={{ color: "#4E4228" }}
              aria-label="Previous month"
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#F0EBE0")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
            >
              <i className="ri-arrow-left-s-line text-xl"></i>
            </button>
            <h3 className="font-serif font-light text-xl md:text-2xl" style={{ color: "#3C3530" }}>
              {MONTH_NAMES[cursor.getMonth()]} {cursor.getFullYear()}
            </h3>
            <button
              onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
              className="w-10 h-10 flex items-center justify-center rounded-full transition-colors cursor-pointer"
              style={{ color: "#4E4228" }}
              aria-label="Next month"
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#F0EBE0")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
            >
              <i className="ri-arrow-right-s-line text-xl"></i>
            </button>
          </div>

          {/* Weekday header */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {WEEKDAYS.map((d) => (
              <div
                key={d}
                className="text-center text-xs font-sans font-medium uppercase tracking-[0.1em] py-2"
                style={{ color: "#8C8478" }}
              >
                {d}
              </div>
            ))}
          </div>

          {/* Day cells */}
          <div className="grid grid-cols-7 gap-1">
            {grid.map((d, i) => {
              if (!d) return <div key={i} className="aspect-square" />;
              const key = toDateKey(d);
              const isUnavailable = unavailable.has(key);
              const isPast = key < todayKey;
              const isToday = key === todayKey;

              let bg = "#F8F3EA";
              let color = "#3C3530";
              let label = "Available";
              if (isPast) {
                bg = "transparent";
                color = "#C8C0B4";
                label = "Past";
              } else if (isUnavailable) {
                bg = "#C8B898";
                color = "#3C3530";
                label = "Unavailable";
              }

              return (
                <div
                  key={i}
                  className="aspect-square flex items-center justify-center text-sm font-sans rounded relative"
                  style={{
                    backgroundColor: bg,
                    color,
                    border: isToday ? "1.5px solid #4E4228" : "1px solid transparent",
                    textDecoration: isPast ? "line-through" : undefined,
                  }}
                  title={label}
                >
                  {d.getDate()}
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex flex-wrap items-center justify-center gap-6 mt-8 text-xs font-sans" style={{ color: "#7E7265" }}>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded" style={{ backgroundColor: "#F8F3EA", border: "1px solid #E4DDD4" }}></span>
              Available
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded" style={{ backgroundColor: "#C8B898" }}></span>
              Unavailable
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded" style={{ border: "1.5px solid #4E4228" }}></span>
              Today
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
